import { errorText } from '../edo/errorText'
import { useSetPrimaryEmployment } from './useUsers'
import { EmploymentList } from './EmploymentList'
import { employmentLabel } from '../edo/forms/EmploymentField'
/**
 * PersonEditor — HR-правка чужой кадровой карточки (спека «Редактирование
 * сотрудников (HR)», 2026-07-01). Right slide-over, same overlay pattern as the
 * people.tsx PersonDrawer (AnimatePresence + scrim + slide from the right).
 *
 * Shown only to hr/admin (`canEditPeople`, UI-gate; the backend re-checks → 403).
 *  • prefills from `GET /users/:id/full` (full card incl. name parts / isActive /
 *    locationId / manager name — none of that is on the public profile)
 *  • sends **only changed fields** to `PATCH /users/:id` (patch-semantics;
 *    `null` clears a nullable field)
 *  • manager picker searches `/users?search=` (≥ 2 chars, 200 ms debounce),
 *    blocks picking the edited person themself; «Снять руководителя» → `managerId: null`
 *  • location pills from `GET /locations` (2 test offices until the 1С catalog lands)
 *  • positions editor is **replace-all**: the whole list is sent; exactly one
 *    `isPrimary` is enforced by the UI (pressing a radio re-primaries the list)
 *  • limits are mirrored client-side for instant feedback; the backend stays
 *    the source of truth and its `message` is surfaced verbatim on 400/403/404
 */
import { useMemo, useState } from 'react'
import { Input, ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../api/client'
import { useApp } from '../../i18n'
import { ymd10 } from '../../lib/format'
import type { DirectoryUserFull, UserAdminPatch, UserPositionInput } from '../../lib/types'
import { palette } from '../../theme/palette'
import { tone } from '../../theme/tones'
import { AnimatePresence, Avatar, Button, Icon, IconButton } from '../../ui'
import { DateField, todayYmd } from '../../ui/DateField'
import { useLocations, useUpdateUser, useUserFull } from './useUsers'
import { useUserSearch } from './useUserSearch'

// ── field limits (mirror of the backend whitelist; истина — на бэке) ──
const LIM = { name: 100, fullName: 200, title: 200, phone: 40, positions: 20, skills: 30, skill: 40 }

/** Draft row of the positions editor ('' department → null on send). */
interface PosDraft {
  title: string
  department: string
  isPrimary: boolean
}

/** Trimmed string → null when empty (patch semantics: null clears the field). */
const orNull = (s: string): string | null => s.trim() || null

// ── small shared bits ─────────────────────────────────────────────────────────
function SectionTitle({ children }: { children: string }) {
  return (
    <Text fontSize={11} fontWeight="700" color="$tertiary" letterSpacing={0.6} textTransform="uppercase" marginTop={18} marginBottom={8}>
      {children}
    </Text>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <YStack gap={4} marginBottom={10}>
      <Text fontSize={11} color="$muted">
        {label}
      </Text>
      {children}
    </YStack>
  )
}

/** The shared text-input skin (mirrors profile.tsx edit inputs). */
function TextInput(props: React.ComponentProps<typeof Input>) {
  return (
    <Input
      height={34}
      fontSize={13}
      color="$color"
      backgroundColor="$bgLayout"
      borderWidth={1}
      borderColor="$borderColor"
      borderRadius={6}
      paddingHorizontal={9}
      placeholderTextColor="$tertiary"
      focusStyle={{ borderColor: '$primary' }}
      {...props}
    />
  )
}

/** Tiny switch (same look as the settings.tsx one; local — that one isn't exported). */
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Stack
      onPress={() => onChange(!checked)}
      cursor="pointer"
      animation="quick"
      width={40}
      height={22}
      borderRadius={999}
      backgroundColor={checked ? '$primary' : '$borderColor'}
      padding={2}
      justifyContent="center"
    >
      <Stack animation="quick" width={18} height={18} borderRadius={999} backgroundColor="#fff" x={checked ? 18 : 0} />
    </Stack>
  )
}

// ── manager picker ────────────────────────────────────────────────────────────
function ManagerPicker({
  selfId,
  managerId,
  managerName,
  onPick,
  onClear,
}: {
  selfId: string
  managerId: string | null
  managerName: string | null
  onPick: (id: string, name: string) => void
  onClear: () => void
}) {
  const { l } = useApp()
  const { q, setQ, debounced, hits } = useUserSearch()
  // Нельзя выбрать самого редактируемого сотрудника (бэк ответит 400 и на подчинённого — цикл).
  const options = useMemo(() => (hits ?? []).filter((u) => u.id !== selfId).slice(0, 6), [hits, selfId])

  return (
    <YStack gap={8}>
      {managerId && managerName ? (
        <XStack alignItems="center" gap={8} backgroundColor="$bgLayout" borderRadius={7} padding={8}>
          <Avatar name={managerName} seed={managerId} size="sm" />
          <Text flex={1} fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
            {managerName}
          </Text>
          <Button size="sm" onPress={onClear} icon={<Icon name="x" size={13} color="$muted" />}>
            {l('pe.removeManager')}
          </Button>
        </XStack>
      ) : (
        <Text fontSize={12} color="$tertiary">
          {l('pe.noManager')}
        </Text>
      )}
      <TextInput value={q} onChangeText={setQ} placeholder={l('pe.searchManager')} />
      {debounced.length >= 2 && options.length > 0 && (
        <YStack borderWidth={1} borderColor="$borderSecondary" borderRadius={7} overflow="hidden">
          {options.map((u, i) => (
            <XStack
              key={u.id}
              onPress={() => {
                onPick(u.id, u.fullName)
                setQ('')
              }}
              cursor="pointer"
              alignItems="center"
              gap={8}
              padding={8}
              borderTopWidth={i === 0 ? 0 : 1}
              borderColor="$borderSecondary"
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
            >
              <Avatar name={u.fullName} seed={u.id} size="sm" />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} color="$color" numberOfLines={1}>
                  {u.fullName}
                </Text>
                {!!u.jobTitle && (
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {u.jobTitle}
                  </Text>
                )}
              </YStack>
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  )
}

// ── date field + clear (nullable dates: '' draft ⇒ null on send) ─────────────
function ClearableDate({ value, onChange, max }: { value: string; onChange: (v: string) => void; max?: string }) {
  return (
    <XStack gap={6} alignItems="center">
      <Stack flex={1}>
        <DateField value={value} onChange={onChange} min="1950-01-01" max={max} />
      </Stack>
      {!!value && (
        <IconButton onPress={() => onChange('')} borderWidth={1} borderColor="$borderColor">
          <Icon name="x" size={13} color="$muted" />
        </IconButton>
      )}
    </XStack>
  )
}

// ── the editor form (mounted only when the full card is loaded) ──────────────
function EditorForm({ full, onClose }: { full: DirectoryUserFull; onClose: () => void }) {
  const { l, t } = useApp()
  const primary = useSetPrimaryEmployment(full.id)
  const [draftBlocked, setDraftBlocked] = useState(false)

  const [fullName, setFullName] = useState(full.fullName)
  const [lastName, setLastName] = useState(full.lastName ?? '')
  const [firstName, setFirstName] = useState(full.firstName ?? '')
  const [middleName, setMiddleName] = useState(full.middleName ?? '')
  const [jobTitle, setJobTitle] = useState(full.jobTitle ?? '')
  const [department, setDepartment] = useState(full.department ?? '')
  const [grade, setGrade] = useState(full.grade == null ? '' : String(full.grade))
  const [managerId, setManagerId] = useState<string | null>(full.managerId)
  // Руководитель сотрудника с местом работы в 1С — из штатного расписания (решение 14.10.2026):
  // API такую смену отбивает 400, поэтому поле не предлагаем. Грузия и внешние — без мест работы.
  const managerFrom1c = (full.employments?.length ?? 0) > 0
  const [managerName, setManagerName] = useState<string | null>(full.manager?.fullName ?? null)
  const [locationId, setLocationId] = useState<string | null>(full.locationId)
  const [hiredAt, setHiredAt] = useState(ymd10(full.hiredAt))
  const [birthday, setBirthday] = useState(ymd10(full.birthday))
  const [isActive, setIsActive] = useState(full.isActive)
  const [showInOrgTree, setShowInOrgTree] = useState(full.showInOrgTree)
  const [phone, setPhone] = useState(full.phone ?? '')
  const [skills, setSkills] = useState<string[]>(full.skills)
  const [newSkill, setNewSkill] = useState('')
  const [positions, setPositions] = useState<PosDraft[]>(
    full.positions.map((p) => ({ title: p.title, department: p.department ?? '', isPrimary: p.isPrimary })),
  )
  const [validationError, setValidationError] = useState<string | null>(null)

  const { data: locations } = useLocations()
  const update = useUpdateUser(full.id, onClose)

  // ── positions editor ──
  const setPos = (i: number, patch: Partial<PosDraft>) =>
    setPositions((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)))
  // Ровно один primary: выбор нового снимает флаг с остальных.
  const setPrimary = (i: number) => setPositions((prev) => prev.map((p, idx) => ({ ...p, isPrimary: idx === i })))
  const removePos = (i: number) =>
    setPositions((prev) => {
      const next = prev.filter((_, idx) => idx !== i)
      // если удалили primary — назначить первую оставшуюся
      if (next.length && !next.some((p) => p.isPrimary)) next[0] = { ...next[0], isPrimary: true }
      return next
    })
  const addPos = () =>
    setPositions((prev) =>
      prev.length >= LIM.positions ? prev : [...prev, { title: '', department: '', isPrimary: prev.length === 0 }],
    )

  // ── skills chips (same UX as profile.tsx) ──
  const addSkill = () => {
    const s = newSkill.trim()
    if (!s || s.length > LIM.skill || skills.includes(s) || skills.length >= LIM.skills) return
    setSkills((p) => [...p, s])
    setNewSkill('')
  }

  /** Только изменённые поля — patch-семантика (§4 спеки). */
  const buildPatch = (): UserAdminPatch => {
    const patch: UserAdminPatch = {}
    if (fullName.trim() !== full.fullName) patch.fullName = fullName.trim()
    if (orNull(lastName) !== full.lastName) patch.lastName = orNull(lastName)
    if (orNull(firstName) !== full.firstName) patch.firstName = orNull(firstName)
    if (orNull(middleName) !== full.middleName) patch.middleName = orNull(middleName)
    if (orNull(jobTitle) !== full.jobTitle) patch.jobTitle = orNull(jobTitle)
    if (orNull(department) !== full.department) patch.department = orNull(department)
    const gradeVal = grade.trim() === '' ? null : Number(grade.trim())
    if (gradeVal !== full.grade) patch.grade = gradeVal
    if (managerId !== full.managerId) patch.managerId = managerId
    if (locationId !== full.locationId) patch.locationId = locationId
    if ((hiredAt || null) !== (ymd10(full.hiredAt) || null)) patch.hiredAt = hiredAt || null
    if ((birthday || null) !== (ymd10(full.birthday) || null)) patch.birthday = birthday || null
    if (isActive !== full.isActive) patch.isActive = isActive
    if (showInOrgTree !== full.showInOrgTree) patch.showInOrgTree = showInOrgTree
    if (orNull(phone) !== full.phone) patch.phone = orNull(phone)
    if (JSON.stringify(skills) !== JSON.stringify(full.skills)) patch.skills = skills
    const posInput: UserPositionInput[] = positions.map((p) => ({
      title: p.title.trim(),
      department: orNull(p.department),
      isPrimary: p.isPrimary,
    }))
    const posInitial: UserPositionInput[] = full.positions.map((p) => ({
      title: p.title,
      department: p.department,
      isPrimary: p.isPrimary,
    }))
    if (JSON.stringify(posInput) !== JSON.stringify(posInitial)) patch.positions = posInput
    if (['jobTitle', 'department', 'grade', 'managerId', 'locationId', 'hiredAt', 'isActive', 'positions'].some(field => patch[field as keyof UserAdminPatch] !== undefined)) {
      patch.expectedPrimaryEmploymentId = full.employments?.find(job => job.isPrimary && job.status === 'ACTIVE')?.id ?? null
    }
    return patch
  }

  /** Клиентские лимиты — мгновенный фидбек; истина всё равно на бэке. */
  const validate = (): string | null => {
    if (!fullName.trim()) return l('pe.errFullName')
    if (fullName.trim().length > LIM.fullName) return l('pe.errFullName')
    for (const s of [lastName, firstName, middleName]) if (s.trim().length > LIM.name) return l('pe.errLong')
    for (const s of [jobTitle, department]) if (s.trim().length > LIM.title) return l('pe.errLong')
    if (phone.trim().length > LIM.phone) return l('pe.errLong')
    const g = grade.trim()
    if (g !== '' && (!/^\d+$/.test(g) || Number(g) > 100)) return l('pe.errGrade')
    if (positions.some((p) => !p.title.trim() || p.title.trim().length > LIM.title)) return l('pe.errPosTitle')
    const titles = positions.map((p) => p.title.trim())
    if (new Set(titles).size !== titles.length) return l('pe.errPosDup')
    return null
  }

  const save = () => {
    if (draftBlocked) return
    if (update.isPending) return
    const err = validate()
    setValidationError(err)
    if (err) return
    const patch = buildPatch()
    if (Object.keys(patch).length === 0) {
      onClose() // ничего не изменилось — бэк всё равно бы ответил текущим профилем без аудита
      return
    }
    update.mutate(patch)
  }

  const saveError = validationError ?? (update.error ? t(errorText(update.error)) : null)

  return (
    <>
      <ScrollView flex={1}>
        <YStack paddingHorizontal={20} paddingBottom={16}>
          {/* who is being edited */}
          <XStack alignItems="center" gap={10} paddingVertical={14}>
            <Avatar name={full.fullName} seed={full.id} size="sm" uri={api.assetUrl(full.avatarUrl)} />
            <YStack flex={1} minWidth={0}>
              <Text fontSize={14} fontWeight="700" color="$color" numberOfLines={1}>
                {full.fullName}
              </Text>
              <Text fontSize={11} color="$muted" numberOfLines={1}>
                {full.email}
              </Text>
            </YStack>
          </XStack>
          {full.orgManuallyEdited && (
            <XStack alignItems="center" gap={6} backgroundColor="$bgLayout" borderRadius={7} padding={8}>
              <Icon name="pencil" size={13} color="$tertiary" />
              <Text flex={1} fontSize={11} color="$muted">
                {l('pe.manual')}
              </Text>
            </XStack>
          )}

          {/* ФИО */}
          <SectionTitle>{l('pe.identity')}</SectionTitle>
          <Field label={l('pe.fullName')}>
            <TextInput value={fullName} onChangeText={setFullName} maxLength={LIM.fullName} />
          </Field>
          <Field label={l('pe.lastName')}>
            <TextInput value={lastName} onChangeText={setLastName} maxLength={LIM.name} />
          </Field>
          <Field label={l('pe.firstName')}>
            <TextInput value={firstName} onChangeText={setFirstName} maxLength={LIM.name} />
          </Field>
          <Field label={l('pe.middleName')}>
            <TextInput value={middleName} onChangeText={setMiddleName} maxLength={LIM.name} />
          </Field>

          {/* Работа */}
          <SectionTitle>{l('pe.work')}</SectionTitle>
          <EmploymentList jobs={full.employments} conflict={full.employmentSelectionRequired} />
          {(full.employments ?? []).filter(job => job.status === 'ACTIVE').map(job => <Button key={job.id} disabled={draftBlocked || update.isPending || job.isPrimary} onPress={() => { setDraftBlocked(true); primary.mutate(job.id) }}>{t({ ru: 'Сделать основным', az: 'Əsas seç', en: 'Make primary' })}: {employmentLabel(job)}</Button>)}
          {primary.error && <Text color={tone('red').text}>{t(errorText(primary.error))} · {t({ ru: 'Закройте и снова откройте карточку, чтобы загрузить актуальные данные.', az: 'Yeni məlumatları yükləmək üçün kartı yenidən açın.', en: 'Close and reopen the card to load the current data.' })}</Text>}

          <Field label={l('pe.jobTitle')}>
            <TextInput value={jobTitle} onChangeText={setJobTitle} maxLength={LIM.title} />
          </Field>
          <Field label={l('pe.department')}>
            <TextInput value={department} onChangeText={setDepartment} maxLength={LIM.title} />
          </Field>
          <Field label={l('pe.grade')}>
            <TextInput value={grade} onChangeText={setGrade} inputMode="numeric" maxLength={3} width={90} />
          </Field>

          {/* Должности-совместители — replace-all */}
          <SectionTitle>{l('pe.positions')}</SectionTitle>
          <YStack gap={8}>
            {positions.map((p, i) => (
              <XStack key={i} gap={6} alignItems="center">
                {/* primary radio — ровно одна основная */}
                <Stack
                  onPress={() => setPrimary(i)}
                  cursor="pointer"
                  width={18}
                  height={18}
                  borderRadius={999}
                  borderWidth={2}
                  borderColor={p.isPrimary ? '$primary' : '$borderColor'}
                  alignItems="center"
                  justifyContent="center"
                  accessibilityLabel={l('pe.posPrimary')}
                >
                  {p.isPrimary && <Stack width={8} height={8} borderRadius={999} backgroundColor="$primary" />}
                </Stack>
                <Stack flex={3}>
                  <TextInput
                    value={p.title}
                    onChangeText={(v: string) => setPos(i, { title: v })}
                    placeholder={l('pe.posTitle')}
                    maxLength={LIM.title}
                  />
                </Stack>
                <Stack flex={2}>
                  <TextInput
                    value={p.department}
                    onChangeText={(v: string) => setPos(i, { department: v })}
                    placeholder={l('pe.posDept')}
                    maxLength={LIM.title}
                  />
                </Stack>
                <IconButton onPress={() => removePos(i)}>
                  <Icon name="x" size={14} color="$muted" />
                </IconButton>
              </XStack>
            ))}
            {positions.length < LIM.positions && (
              <Button size="sm" alignSelf="flex-start" onPress={addPos} icon={<Icon name="plus" size={13} color="$muted" />}>
                {l('pe.addPosition')}
              </Button>
            )}
          </YStack>

          {/* Оргструктура */}
          <SectionTitle>{l('pe.org')}</SectionTitle>
          <Field label={l('pe.manager')}>
            {managerFrom1c ? (
              <YStack gap={4}>
                {full.managerId && managerName ? (
                  <XStack alignItems="center" gap={8} backgroundColor="$bgLayout" borderRadius={7} padding={8}>
                    <Avatar name={managerName} seed={full.managerId} size="sm" />
                    <Text flex={1} fontSize={13} fontWeight="600" color="$color" numberOfLines={1}>
                      {managerName}
                    </Text>
                  </XStack>
                ) : (
                  <Text fontSize={12} color="$tertiary">
                    {l('pe.noManager')}
                  </Text>
                )}
                <Text fontSize={12} color="$tertiary">
                  {l('pe.managerFrom1c')}
                </Text>
              </YStack>
            ) : (
              <ManagerPicker
                selfId={full.id}
                managerId={managerId}
                managerName={managerName}
                onPick={(id, name) => {
                  setManagerId(id)
                  setManagerName(name)
                }}
                onClear={() => {
                  setManagerId(null)
                  setManagerName(null)
                }}
              />
            )}
          </Field>
          <Field label={l('pe.location')}>
            <XStack flexWrap="wrap" gap={6}>
              <Button size="sm" variant={locationId == null ? 'primary' : 'default'} onPress={() => setLocationId(null)}>
                {l('pe.noLocation')}
              </Button>
              {(locations ?? []).map((loc) => (
                <Button
                  key={loc.id}
                  size="sm"
                  variant={locationId === loc.id ? 'primary' : 'default'}
                  onPress={() => setLocationId(loc.id)}
                  icon={<Icon name="map-pin" size={12} color={locationId === loc.id ? '$onPrimary' : '$muted'} />}
                >
                  {loc.city && loc.city !== loc.name ? `${loc.name} · ${loc.city}` : loc.name}
                </Button>
              ))}
            </XStack>
          </Field>
          <XStack alignItems="center" gap={12} marginTop={4}>
            <Toggle checked={showInOrgTree} onChange={setShowInOrgTree} />
            <YStack flex={1}>
              <Text fontSize={13} fontWeight="600" color="$color">
                {l('pe.showInOrgTree')}
              </Text>
              <Text fontSize={11} color="$muted">
                {l('pe.showInOrgTreeHint')}
              </Text>
            </YStack>
          </XStack>

          {/* Даты */}
          <SectionTitle>{l('pe.dates')}</SectionTitle>
          <Field label={l('pe.hiredAt')}>
            <ClearableDate value={hiredAt} onChange={setHiredAt} />
          </Field>
          <Field label={l('pe.birthday')}>
            {/* дата рождения не бывает в будущем; дата найма — бывает (плановый найм) */}
            <ClearableDate value={birthday} onChange={setBirthday} max={todayYmd()} />
          </Field>

          {/* Контакты */}
          <SectionTitle>{l('pe.contacts')}</SectionTitle>
          <Field label={l('pe.phone')}>
            <TextInput value={phone} onChangeText={setPhone} inputMode="tel" maxLength={LIM.phone} />
          </Field>
          <Field label={l('profile.skills')}>
            <XStack flexWrap="wrap" gap={6}>
              {skills.map((s) => (
                <XStack
                  key={s}
                  alignItems="center"
                  gap={5}
                  backgroundColor={palette.navySoft}
                  borderRadius={999}
                  paddingLeft={10}
                  paddingRight={6}
                  paddingVertical={3}
                >
                  <Text fontSize={12} fontWeight="600" color="$primary">
                    {s}
                  </Text>
                  <Stack onPress={() => setSkills((p) => p.filter((x) => x !== s))} cursor="pointer" hoverStyle={{ opacity: 0.6 }}>
                    <Icon name="x" size={13} color="$primary" />
                  </Stack>
                </XStack>
              ))}
            </XStack>
            <XStack gap={8} marginTop={6} alignItems="center">
              <Stack flex={1}>
                <TextInput
                  value={newSkill}
                  onChangeText={setNewSkill}
                  onSubmitEditing={addSkill}
                  placeholder={l('profile.addSkill')}
                  maxLength={LIM.skill}
                />
              </Stack>
              <Button size="sm" onPress={addSkill} icon={<Icon name="plus" size={14} color="$primary" />} />
            </XStack>
          </Field>

          {/* Активность */}
          <SectionTitle>{l('pe.activity')}</SectionTitle>
          <XStack alignItems="center" gap={12}>
            <Toggle checked={isActive} onChange={setIsActive} />
            <YStack flex={1}>
              <Text fontSize={13} fontWeight="600" color="$color">
                {l('pe.active')}
              </Text>
              <Text fontSize={11} color="$muted">
                {l('pe.activeHint')}
              </Text>
            </YStack>
          </XStack>
        </YStack>
      </ScrollView>

      {/* footer: error + actions */}
      <YStack padding={16} gap={10} borderTopWidth={1} borderColor="$borderSecondary" backgroundColor="$background">
        {!!saveError && (
          <Text fontSize={12} color={tone('red').text}>
            {saveError}
          </Text>
        )}
        <XStack gap={8}>
          <Button block onPress={onClose}>
            {l('c.cancel')}
          </Button>
          <Button block variant="primary" onPress={save} disabled={draftBlocked || update.isPending} opacity={update.isPending || draftBlocked ? 0.6 : 1}>
            {l('c.save')}
          </Button>
        </XStack>
      </YStack>
    </>
  )
}

// ── overlay shell ─────────────────────────────────────────────────────────────
export function PersonEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { l } = useApp()
  const { data: full, isLoading, isFetching, error } = useUserFull(id)

  return (
    <AnimatePresence>
      {id && (
        <Stack key="person-editor" position="absolute" top={0} left={0} right={0} bottom={0} zIndex={120}>
          <Stack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor={palette.scrimSoft}
            onPress={onClose}
            animation="quicker"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            opacity={1}
          />
          <YStack
            animation="quicker"
            enterStyle={{ x: 460, opacity: 0 }}
            exitStyle={{ x: 460, opacity: 0 }}
            x={0}
            opacity={1}
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            width={440}
            maxWidth="100%"
            backgroundColor="$background"
            borderLeftWidth={1}
            borderColor="$borderSecondary"
            shadowColor={palette.shadow}
            shadowOpacity={0.18}
            shadowRadius={24}
            shadowOffset={{ width: -8, height: 0 }}
          >
            {/* header */}
            <XStack alignItems="center" paddingHorizontal={20} paddingVertical={14} borderBottomWidth={1} borderColor="$borderSecondary">
              <Text flex={1} fontSize={15} fontWeight="700" color="$color">
                {l('pe.title')}
              </Text>
              <IconButton onPress={onClose}>
                <Icon name="x" size={18} color="$muted" />
              </IconButton>
            </XStack>

            {full && !isFetching && !error ? (
              // key: ремаунт формы при смене сотрудника — свежий префилл без эффектов
              <EditorForm key={`${full.id}:${full.employments?.find(job => job.isPrimary)?.id ?? ''}:${full.employmentSelectionRequired ?? false}`}  full={full} onClose={onClose} />
            ) : (
              <YStack flex={1} alignItems="center" justifyContent="center" padding={24}>
                <Text fontSize={13} color="$muted" textAlign="center">
                  {isLoading ? l('pe.loading') : error instanceof Error ? error.message : l('pe.loading')}
                </Text>
              </YStack>
            )}
          </YStack>
        </Stack>
      )}
    </AnimatePresence>
  )
}
